/**
 * useRestoreScrollOnReturn — puts a list back where the user left it after the
 * composer sent them away and back.
 *
 * `/create` is a page, not an overlay, so editing a post unmounts the list it
 * was started from; without this the user lands on the top of the feed and has
 * to find their post again. The offset travels in router state
 * (`restoreScrollY`), so only the trip back from the composer scrolls — every
 * other way into the screen still opens at the top.
 *
 * `ready` must report that the list itself is on screen: scrolling while the
 * skeleton is up would be clamped to its much shorter height.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { readRestoreScrollY } from './createPostRoute';

/**
 * How long the offset keeps being re-applied. A list can still be growing after
 * its first paint (media boxes, fonts), and a screen may run a reset of its own
 * on mount — Home restores its per-tab offset — so one frame is not enough.
 */
const SETTLE_TIMEOUT_MS = 600;

/** Anything the user does themselves ends the restore — it must not fight them. */
const INTERRUPTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

export function useRestoreScrollOnReturn(ready: boolean): void {
  const location = useLocation();
  const target = readRestoreScrollY(location.state);
  // Set only once the offset has been applied for the full settle window, or the
  // user took over. Cleanup deliberately leaves it alone: React remounts effects
  // in development, and a run that was torn down never restored anything.
  const settledRef = useRef(false);

  useEffect(() => {
    if (!ready || target <= 0 || settledRef.current) return;

    let frame = 0;
    let stopped = false;
    const deadline = Date.now() + SETTLE_TIMEOUT_MS;

    const stopApplying = () => {
      stopped = true;
      window.cancelAnimationFrame(frame);
    };

    /** The user scrolling or typing wins — the offset stops chasing them. */
    const yieldToUser = () => {
      stopApplying();
      settledRef.current = true;
    };

    const attempt = () => {
      if (stopped) return;
      window.scrollTo({ top: target, behavior: 'auto' });
      if (Date.now() < deadline) {
        frame = window.requestAnimationFrame(attempt);
      } else {
        settledRef.current = true;
      }
    };

    frame = window.requestAnimationFrame(attempt);
    INTERRUPTS.forEach(event =>
      window.addEventListener(event, yieldToUser, { passive: true }),
    );

    return () => {
      stopApplying();
      INTERRUPTS.forEach(event => window.removeEventListener(event, yieldToUser));
    };
  }, [ready, target]);
}
